'use client';

import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';
import {
  Activity,
  Award,
  CheckCircle2,
  Copy,
  Download,
  ExternalLink,
  FileCheck2,
  Hash,
  Landmark,
  LayoutDashboard,
  Link2,
  Loader2,
  Lock,
  Settings,
  ShieldCheck,
  Star,
  User,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { toast } from 'sonner';

interface Props {
  vaultId: string;
}

export default function PublicVaultContent({ vaultId }: Props) {
  const supabase = createClient();
  const { user } = useAuth();
  const [profileData, setProfileData] = useState<any>(null);
  const [credentials, setCredentials] = useState<any[]>([]);
  const [achievements, setAchievements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: profile } = await supabase
          .rpc('resolve_public_profile_identity_by_vault', { p_vault_id: vaultId })
          .single();

        if (!profile) {
          setLoading(false);
          return;
        }
        setProfileData({
          ...profile,
          full_name: profile.display_name,
          created_at: profile.member_since,
        });

        // Track vault view (fire-and-forget)
        fetch('/api/vault/view', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ vault_owner_id: profile.id }),
        }).catch(() => {});

        const [achRes, verRes] = await Promise.all([
          supabase
            .from('achievements')
            .select('id, title, pillar, institution, status, achievement_date, description')
            .eq('user_id', profile.id)
            .eq('is_public', true)
            .eq('status', 'verified')
            .order('submitted_date', { ascending: false }),
          supabase
            .from('verifications')
            .select(
              'id, verification_ref, verified_at, is_heritage, verified_by, achievements!inner(title, institution, pillar, description, user_id, is_public, status)'
            )
            .eq('achievements.user_id', profile.id)
            .eq('achievements.is_public', true)
            .eq('achievements.status', 'verified')
            .neq('verification_ref', '')
            .order('verified_at', { ascending: false }),
        ]);

        setAchievements(achRes.data || []);
        setCredentials(
          (verRes.data || []).map((v: any) => ({
            id: v.id,
            verification_ref: v.verification_ref,
            title: v.achievements?.title || '',
            institution: v.achievements?.institution || '',
            pillar: v.achievements?.pillar || '',
            description: v.achievements?.description || '',
            verified_at: v.verified_at,
            is_heritage: v.is_heritage,
            verified_by: v.verified_by,
          }))
        );
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [vaultId]);

  const isOwner = !!user && !!profileData && user.id === profileData.id;

  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://ledger.ertzyx.com';
  const vaultUrl = `${siteUrl}/vault/${vaultId}`;
  const profileUrl = profileData?.public_handle
    ? `${siteUrl}/u/${profileData.public_handle}`
    : null;
  const publicRecords = achievements.length;
  const proofCards = 0;
  const verifiedItems =
    credentials.length + achievements.filter((item) => item.status === 'verified').length;
  const pillarsActive = new Set(
    [...credentials.map((item) => item.pillar), ...achievements.map((item) => item.pillar)].filter(
      Boolean
    )
  ).size;

  const copyVaultLink = () => {
    navigator.clipboard.writeText(vaultUrl).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Vault link copied');
  };

  const shareVault = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${profileData?.full_name || 'Ertzyx'} Public Vault`,
          text: 'View this public proof vault on Ertzyx.',
          url: vaultUrl,
        });
        return;
      } catch {
        /* cancelled */
      }
    }
    copyVaultLink();
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100">
        <div className="flex items-center gap-3 text-sm font-semibold text-slate-500">
          <Loader2 size={18} className="animate-spin" />
          Loading public vault...
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-100 p-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-slate-400 shadow-sm">
          <Lock size={24} />
        </div>
        <h1 className="text-xl font-bold text-slate-800">Vault not found</h1>
        <p className="max-w-sm text-sm text-slate-500">
          This vault does not exist or is not available as a public proof page.
        </p>
        <Link href="/" className="rounded-xl bg-trust-blue px-4 py-2 text-sm font-bold text-white">
          Back to Ertzyx
        </Link>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-slate-100 text-slate-900">
      {/* Owner management bar — only visible to the vault owner */}
      {isOwner && (
        <div
          style={{
            background: '#0d1117',
            color: '#e8edf3',
            padding: '10px 16px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 12,
          }}
        >
          <span style={{ fontWeight: 600, color: '#4a5d72' }}>Your public vault</span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <Link
              href="/individual-dashboard"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '6px 10px',
                borderRadius: 7,
                background: 'rgba(255,255,255,0.08)',
                color: '#e8edf3',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              <LayoutDashboard size={12} /> Dashboard
            </Link>
            <Link
              href="/individual-dashboard/public-identity?tab=vault"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '6px 10px',
                borderRadius: 7,
                background: 'rgba(0,180,160,0.12)',
                color: '#00b4a0',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              <Settings size={12} /> Manage Vault
            </Link>
            <button
              type="button"
              onClick={copyVaultLink}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '6px 10px',
                borderRadius: 7,
                border: 'none',
                background: '#00b4a0',
                color: '#fff',
                fontWeight: 600,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              <Copy size={12} /> {copied ? 'Copied!' : 'Copy link'}
            </button>
          </div>
        </div>
      )}

      {/* Logged-in visitor bar — shown to authenticated non-owners */}
      {!!user && !isOwner && (
        <div
          style={{
            background: '#0d1117',
            color: '#e8edf3',
            padding: '9px 16px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 12,
          }}
        >
          <Link
            href="/individual-dashboard"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              color: '#00b4a0',
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: 12,
            }}
          >
            <LayoutDashboard size={13} /> My Ledger
          </Link>
          {profileData?.public_handle && (
            <Link
              href={`/u/${profileData.public_handle}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '5px 10px',
                borderRadius: 7,
                background: 'rgba(255,255,255,0.06)',
                color: '#8ba3bc',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: 11,
              }}
            >
              View profile
            </Link>
          )}
        </div>
      )}

      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
          <Link href={user ? '/individual-dashboard' : '/'} className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="text-sm font-bold text-trust-blue">Ertzyx</span>
          </Link>
          <span className="hidden items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700 sm:inline-flex">
            <ShieldCheck size={12} /> Public Vault
          </span>
          <button
            type="button"
            onClick={copyVaultLink}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600"
          >
            <Copy size={12} /> {copied ? 'Copied' : 'Copy Vault Link'}
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl space-y-4 px-4 py-5 sm:px-6">
        <section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_260px]">
          {/* Hero identity card */}
          <div className="overflow-hidden rounded-2xl bg-[#050f1c] shadow-lg" style={{ border: '1px solid rgba(0,180,160,0.12)' }}>
            {/* Accent top bar */}
            <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg,#00b4a0 0%,rgba(0,180,160,0.3) 60%,transparent 100%)' }} />
            {/* Grid texture */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.04] [background-image:linear-gradient(rgba(255,255,255,.7)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.7)_1px,transparent_1px)] [background-size:32px_32px]" style={{ position: 'relative' }} />
            <div className="relative px-5 py-6 sm:px-7">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="mb-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em]" style={{ background: 'rgba(0,180,160,0.1)', border: '1px solid rgba(0,180,160,0.22)', color: '#5dcaa5' }}>
                    <ShieldCheck size={11} /> Public Vault
                  </div>
                  <h1 className="text-2xl font-bold tracking-tight text-white sm:text-3xl" style={{ fontFamily: 'DM Serif Display, serif', letterSpacing: '-0.02em' }}>
                    {profileData.full_name}
                  </h1>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1 font-mono text-xs font-bold text-emerald-300">
                      <Hash size={10} /> {vaultId}
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-400/20 bg-emerald-400/8 px-2.5 py-1 text-xs font-semibold text-emerald-300">
                      <CheckCircle2 size={10} /> Verification active
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-lg border border-white/8 bg-white/4 px-2.5 py-1 text-xs font-semibold text-slate-300">
                      <Lock size={10} /> Public records only
                    </span>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-slate-400" style={{ maxWidth: 480 }}>
                    This is a formal public proof vault. It shows only verified records and evidence the owner has chosen to make public.
                  </p>
                </div>

                <div className="flex flex-wrap gap-2 sm:flex-col sm:items-end">
                  <button
                    type="button"
                    onClick={copyVaultLink}
                    className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-colors"
                    style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: copied ? '#4ade80' : '#e2eaf2' }}
                  >
                    {copied ? <><CheckCircle2 size={13} /> Copied</> : <><Copy size={13} /> Copy Vault Link</>}
                  </button>
                  <button
                    type="button"
                    onClick={shareVault}
                    className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold"
                    style={{ background: 'rgba(0,180,160,0.12)', border: '1px solid rgba(0,180,160,0.22)', color: '#5dcaa5' }}
                  >
                    <ExternalLink size={13} /> Share Vault
                  </button>
                  {profileUrl && (
                    <a
                      href={profileUrl}
                      className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#94a3b8' }}
                    >
                      <User size={13} /> View Public Profile
                    </a>
                  )}
                </div>
              </div>

              {/* Disclosure info strip */}
              <div className="mt-5 grid gap-3 border-t border-white/6 pt-4 sm:grid-cols-2">
                <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Disclosure</p>
                  <p className="mt-1 text-sm font-semibold text-slate-200">Public proof records only</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-slate-500">Private records are never shown unless published by the owner.</p>
                </div>
                <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Vault Link</p>
                  <p className="mt-1 truncate font-mono text-sm font-semibold text-slate-200">{vaultUrl.replace('https://', '')}</p>
                  <p className="mt-0.5 text-xs text-slate-500">No account needed to view public proof.</p>
                </div>
              </div>
            </div>
          </div>

          <QrCard vaultId={vaultId} vaultUrl={vaultUrl} />
        </section>

        {/* Metrics */}
        <section className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
          <Metric label="Public Records" value={publicRecords} icon={<Award size={14} />} />
          <Metric label="Credentials" value={credentials.length} icon={<Landmark size={14} />} />
          <Metric label="Proof Cards" value={proofCards} icon={<FileCheck2 size={14} />} />
          <Metric label="Verified" value={verifiedItems} icon={<CheckCircle2 size={14} />} />
          <Metric label="Pillars" value={pillarsActive} icon={<Star size={14} />} />
        </section>

        <Section title="About This Vault" icon={<ShieldCheck size={16} />}>
          <p className="text-sm leading-relaxed text-slate-600">
            This public vault shows only verified records, credentials, and evidence that the owner has chosen to disclose. It is a structured proof surface, distinct from the owner&apos;s public story profile.
          </p>
        </Section>

        <section className="grid gap-4 lg:grid-cols-2">
          <ChecklistCard
            title="What appears in this vault"
            icon={<FileCheck2 size={16} />}
            items={[
              'Verified achievements and awards',
              'Institution-reviewed credentials',
              'Proof cards and evidence summaries',
              'Public documents and records',
              'Verification history and witness logs',
            ]}
          />
          <ChecklistCard
            title="Privacy and disclosure"
            icon={<Lock size={16} />}
            items={[
              'Only publicly published records are shown',
              'Private records are never visible here',
              'The owner controls all publication settings',
              'No account required to view public proof',
            ]}
          />
        </section>

        <RecordsSection achievements={achievements} onCopy={copyVaultLink} />
        <div className="grid gap-5 lg:grid-cols-2">
          <CredentialsSection credentials={credentials} />
          <ProofCardsSection />
          <VerificationLinksSection credentials={credentials} />
          <VerificationLogSection credentials={credentials} />
        </div>

        {profileUrl && (
          <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-bold text-slate-800">Public Profile</p>
              <p className="text-xs leading-relaxed text-slate-500">
                The Public Profile shows stories, milestones, and social identity. This vault shows only formal proof and verified records.
              </p>
            </div>
            <a
              href={profileUrl}
              className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600"
            >
              <User size={13} /> View Public Profile
            </a>
          </div>
        )}
      </div>
    </main>
  );
}

function Metric({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
        {icon}
      </div>
      <p className="text-[22px] font-bold tabular-nums text-slate-900" style={{ fontFamily: 'DM Serif Display, serif', letterSpacing: '-0.01em' }}>{value}</p>
      <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    </div>
  );
}

function InfoBox({
  label,
  title,
  children,
}: {
  label: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">{label}</p>
      <p className="mt-2 truncate text-sm font-semibold text-slate-800">{title}</p>
      <p className="mt-1 text-xs leading-relaxed text-slate-500">{children}</p>
    </div>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-2 text-emerald-700">
        {icon}
        <h2 className="font-bold text-slate-900">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function QrCard({ vaultId, vaultUrl }: { vaultId: string; vaultUrl: string }) {
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=10&data=${encodeURIComponent(vaultUrl)}`;
  return (
    <aside className="flex flex-col rounded-2xl bg-[#050f1c] p-5 shadow-lg" style={{ border: '1px solid rgba(0,180,160,0.12)' }}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Scan to Verify</p>
          <p className="mt-0.5 text-sm font-bold text-slate-200">Public Vault QR</p>
        </div>
        <div className="flex h-8 w-8 items-center justify-center rounded-xl" style={{ background: 'rgba(0,180,160,0.12)', border: '1px solid rgba(0,180,160,0.2)' }}>
          <ShieldCheck size={15} style={{ color: '#5dcaa5' }} />
        </div>
      </div>
      <div className="mx-auto flex aspect-square w-full max-w-[190px] items-center justify-center overflow-hidden rounded-2xl bg-white p-2" style={{ border: '2px solid rgba(0,180,160,0.18)' }}>
        <img
          src={qrSrc}
          alt={`QR code for vault ${vaultId}`}
          width={190}
          height={190}
          style={{ display: 'block', width: '100%', height: '100%' }}
        />
      </div>
      <p className="mt-3 text-center font-mono text-[11px] font-semibold text-slate-500">{vaultId}</p>
      <p className="mt-1.5 text-center text-[11px] leading-relaxed text-slate-600">
        Opens this formal proof page when scanned.
      </p>
      <a
        href={vaultUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 block truncate text-center text-[11px] font-medium"
        style={{ color: '#5dcaa5' }}
      >
        {vaultUrl.replace('https://', '')}
      </a>
    </aside>
  );
}

function ChecklistCard({
  title,
  icon,
  items,
}: {
  title: string;
  icon: React.ReactNode;
  items: string[];
}) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-2 text-emerald-700">
        {icon}
        <h2 className="font-bold text-slate-900">{title}</h2>
      </div>
      <div className="space-y-3">
        {items.map((item) => (
          <div
            key={item}
            className="flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5"
          >
            <CheckCircle2 size={14} className="mt-0.5 text-emerald-600" />
            <span className="text-sm font-semibold leading-relaxed text-slate-700">{item}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function RecordsSection({ achievements, onCopy }: { achievements: any[]; onCopy: () => void }) {
  if (achievements.length === 0) {
    return (
      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <SectionHeader title="Verified Records" icon={<Award size={15} />} />
        <div className="px-5 py-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50">
            <ShieldCheck size={22} className="text-slate-300" />
          </div>
          <p className="text-sm font-semibold text-slate-700">No public records published yet.</p>
          <p className="mx-auto mt-2 max-w-md text-xs leading-relaxed text-slate-500">
            The owner has not published any verified records to this vault yet. Private records are never shown here.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <button
              onClick={onCopy}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600"
            >
              <Copy size={12} /> Copy Vault Link
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <SectionHeader
        title="Verified Records"
        icon={<Award size={15} />}
        trailing={`${achievements.length} records`}
      />
      <div className="divide-y divide-slate-100">
        {achievements.slice(0, 10).map((item) => (
          <div
            key={item.id}
            className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-slate-800">{item.title}</p>
              <p className="truncate text-xs text-slate-500">
                {item.institution || 'Public evidence record'}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {item.pillar && (
                <span className="rounded-lg bg-slate-100 px-2 py-1 text-xs font-bold text-slate-600">
                  {item.pillar}
                </span>
              )}
              {item.status === 'verified' && (
                <span className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700">
                  <CheckCircle2 size={10} /> Verified
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function CredentialsSection({ credentials }: { credentials: any[] }) {
  if (credentials.length === 0) {
    return (
      <EmptySection
        title="Credentials"
        icon={<Landmark size={15} />}
        text="No institution-reviewed credentials published yet."
      />
    );
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-2 text-emerald-700">
        <Landmark size={16} />
        <h2 className="font-bold text-slate-900">Credentials</h2>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {credentials.map((cred) => (
          <div key={cred.id} className="rounded-xl border border-amber-200 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-start justify-between gap-3">
              <h3 className="text-sm font-bold text-slate-800">{cred.title}</h3>
              <span className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700">
                <CheckCircle2 size={10} /> Verified
              </span>
            </div>
            <p className="text-xs leading-relaxed text-slate-500">{cred.description}</p>
            <div className="mt-4 rounded-lg border border-amber-100 bg-amber-50/70 p-3">
              <p className="text-xs font-bold text-amber-800">Institutional Stamp</p>
              <p className="mt-1 text-xs text-slate-600">{cred.verified_by || cred.institution}</p>
              <p className="mt-1 truncate font-mono text-xs text-slate-400">
                {cred.verification_ref}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ProofCardsSection() {
  return (
    <EmptySection
      title="Proof Cards"
      icon={<FileCheck2 size={15} />}
      text="No proof cards published yet. Proof cards are shareable summaries for individual verified records or credentials."
      action={
        <Link
          href="/login"
          className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-2 text-xs font-bold text-emerald-700"
        >
          <FileCheck2 size={12} /> Create Proof Card
        </Link>
      }
    />
  );
}

function VerificationLinksSection({ credentials }: { credentials: any[] }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <SectionHeader
        title="Public Verification Links"
        icon={<Link2 size={15} />}
        trailing={`${credentials.length} available`}
      />
      {credentials.length > 0 ? (
        <div className="divide-y divide-slate-100">
          {credentials.slice(0, 4).map((cred) => (
            <div
              key={cred.id}
              className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-800">{cred.title}</p>
                <p className="truncate font-mono text-xs text-slate-400">{cred.verification_ref}</p>
              </div>
              <a
                href={`/credential/${cred.verification_ref}`}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600"
              >
                <ExternalLink size={12} /> Open link
              </a>
            </div>
          ))}
        </div>
      ) : (
        <div className="px-5 py-6 text-center">
          <Link2 size={22} className="mx-auto mb-3 text-slate-300" />
          <p className="text-sm font-semibold text-slate-700">
            No public verification links published yet.
          </p>
          <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-slate-400">
            Published credentials and proof cards will create shareable verification links here.
          </p>
        </div>
      )}
    </section>
  );
}

function VerificationLogSection({ credentials }: { credentials: any[] }) {
  if (credentials.length === 0) {
    return (
      <EmptySection
        title="Verification Log"
        icon={<Activity size={15} />}
        text="No public verification events published yet. Institution stamps and verification history will appear here when public credentials are published."
      />
    );
  }

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <SectionHeader
        title="Verification Log"
        icon={<Activity size={15} />}
        trailing="Recent activity"
      />
      <div className="divide-y divide-slate-100">
        {credentials.slice(0, 5).map((cred) => (
          <div key={cred.id} className="flex items-center gap-3 px-5 py-3">
            <CheckCircle2 size={14} className="text-emerald-600" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-bold text-slate-700">{cred.title}</p>
              <p className="truncate text-xs text-slate-400">
                {cred.verified_by || cred.institution}
              </p>
            </div>
            <span className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700">
              Verified
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionHeader({
  title,
  icon,
  trailing,
}: {
  title: string;
  icon: React.ReactNode;
  trailing?: string;
}) {
  return (
    <div className="flex items-center gap-2.5 border-b border-slate-100 px-5 py-3.5">
      <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">{icon}</div>
      <h2 className="text-[13px] font-bold tracking-[-0.01em] text-slate-800">{title}</h2>
      {trailing && <span className="ml-auto rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-500">{trailing}</span>}
    </div>
  );
}

function EmptySection({
  title,
  icon,
  text,
  action,
}: {
  title: string;
  icon: React.ReactNode;
  text: string;
  action?: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <SectionHeader title={title} icon={icon} />
      <div className="px-5 py-6 text-center">
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-50 text-slate-300">
          {icon}
        </div>
        <p className="mx-auto max-w-md text-sm font-semibold leading-relaxed text-slate-700">
          {text}
        </p>
        {action && <div className="mt-4">{action}</div>}
      </div>
    </section>
  );
}
